import React from "react";
import { CheckCircle2, Circle, Clock, Users, User, Trash2 } from "lucide-react";
import { StatusBadge, PriorityIndicator } from "./TaskUI";
import { CATEGORIES } from "../data/constants";

const TaskCard = ({ task, names, onUpdate, onDelete }) => {
  return (
    <div
      className={`
        group bg-white rounded-lg border transition-all duration-300
        ${
          task.status === "done"
            ? "opacity-50 border-stone-100 bg-stone-50/50 grayscale"
            : "border-stone-200 shadow-sm hover:border-stone-300"
        }
        ${
          task.status === "blocked" ? "border-rose-200 ring-1 ring-rose-50" : ""
        }
      `}
    >
      <div className="p-5">
        {/* Card Header */}
        <div className="flex items-start gap-4">
          {/* Completion Toggle */}
          <button
            onClick={() =>
              onUpdate("status", task.status === "done" ? "todo" : "done")
            }
            className={`mt-0.5 flex-shrink-0 transition-colors ${
              task.status === "done"
                ? "text-[#1F6F54]"
                : "text-stone-300 hover:text-[#1F6F54]"
            }`}
          >
            {task.status === "done" ? (
              <CheckCircle2 className="w-6 h-6" />
            ) : (
              <Circle className="w-6 h-6" />
            )}
          </button>

          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex justify-between items-start gap-2">
              <input
                value={task.title}
                onChange={(e) => onUpdate("title", e.target.value)}
                className={`w-full bg-transparent font-semibold text-stone-800 focus:outline-none focus:ring-0 border-b border-transparent focus:border-[#1F6F54] rounded-none px-0 -mt-1 text-lg ${
                  task.status === "done" ? "line-through text-stone-400" : ""
                }`}
                placeholder="Task title..."
              />
              <PriorityIndicator priority={task.priority} />
            </div>

            {/* Meta Controls Row */}
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {/* Status Dropdown */}
              <div className="relative group/status">
                <select
                  value={task.status}
                  onChange={(e) => onUpdate("status", e.target.value)}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                >
                  <option value="todo">To Do</option>
                  <option value="doing">Doing</option>
                  <option value="blocked">Blocked</option>
                  <option value="done">Done</option>
                </select>
                <StatusBadge status={task.status} />
              </div>

              {/* Category Dropdown */}
              <div className="relative">
                <select
                  value={task.category}
                  onChange={(e) => onUpdate("category", e.target.value)}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {(() => {
                  const cat = CATEGORIES.find((c) => c.id === task.category);
                  return (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] border ${
                        cat?.style || ""
                      }`}
                    >
                      {cat?.label}
                    </span>
                  );
                })()}
              </div>

              {/* Owner Toggle */}
              <button
                onClick={() =>
                  onUpdate(
                    "owner",
                    task.owner === "you"
                      ? "partner"
                      : task.owner === "partner"
                      ? "both"
                      : "you"
                  )
                }
                className="flex items-center gap-1.5 text-stone-500 hover:text-[#1F6F54] transition-colors"
              >
                {task.owner === "both" ? (
                  <Users className="w-3 h-3" />
                ) : (
                  <User className="w-3 h-3" />
                )}
                <span className="font-medium">
                  {task.owner === "you"
                    ? names.you
                    : task.owner === "partner"
                    ? names.partner
                    : "Shared"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Definition of Done */}
        <div className="mt-5 pl-10">
          <div className="bg-[#F9FAFA] rounded p-3 border border-stone-100">
            <label className="block text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-1.5">
              Definition of Done
            </label>
            <textarea
              value={task.doneLooksLike}
              onChange={(e) => onUpdate("doneLooksLike", e.target.value)}
              className="w-full bg-transparent text-sm text-stone-600 resize-none focus:outline-none placeholder:text-stone-300 leading-relaxed"
              rows={2}
              placeholder="What exactly does finished look like?"
            />
          </div>
        </div>

        {/* Footer: Time & Delete */}
        <div className="mt-4 pl-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-stone-400 flex-1">
            <Clock className="w-3.5 h-3.5" />
            <input
              value={task.timeWindow}
              onChange={(e) => onUpdate("timeWindow", e.target.value)}
              className="bg-transparent focus:outline-none w-full text-stone-500 placeholder:text-stone-300"
              placeholder="When is this happening?"
            />
          </div>
          <button
            onClick={() => onDelete(task.id)}
            className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-rose-500 transition-all p-1"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
